import type { Block, SearchResponse } from './types'

/**
 * Google reports `total` as an estimate (`totalSize`), often a few results more than really exist. The bundle finds the
 * real total on the server (`exact: true` on the block, option exact_total). The frontend sends no extra request (every
 * request counts against the rate limit); what the requested pages prove is kept per query:
 * - a page with fewer items than `perPage` is the last one: the total is exact;
 * - an empty page after the first: the pages before it are all that exist (at most the estimated ones),
 *   `total` at most `pageCount × perPage`.
 */

export interface SettledTotal {
    total: number
    pageCount: number
    /** True when the last page was seen, so `total` is the real count. */
    exact: boolean
}

/** Settled totals of one query, by block type. */
export type SettledTotals = Map<string, SettledTotal>

export type PagedBlock = Block & { page: number; perPage: number; pageCount: number }

export function isPaged(block: Block): block is PagedBlock {
    return block.success && block.page !== undefined && block.perPage !== undefined && block.pageCount !== undefined && block.perPage > 0
}

/** What this page proves about the total, null when it proves nothing (a full page). */
export function settleFromPage(block: Block): SettledTotal | null {
    if (!isPaged(block)) {
        return null
    }

    // the server found the real total already (bundle option exact_total)
    if (block.exact === true) {
        return { total: block.total, pageCount: block.pageCount, exact: true }
    }

    if (block.items.length >= block.perPage) {
        return null
    }

    // an empty page after the first: the end lies before it (and not after the estimated last page)
    if (block.items.length === 0 && block.page > 1) {
        const pageCount = Math.min(block.page - 1, block.pageCount)

        return { total: Math.min(block.total, pageCount * block.perPage), pageCount, exact: false }
    }

    return { total: (block.page - 1) * block.perPage + block.items.length, pageCount: block.page, exact: true }
}

/** Records what the blocks of a response prove; an exact total wins, otherwise the lower page count. */
export function recordSettled(response: SearchResponse, settled: SettledTotals): void {
    for (const block of response.blocks) {
        const result = settleFromPage(block)
        const known = settled.get(block.type)

        if (!result || known?.exact) {
            continue
        }

        if (!known || result.exact || result.pageCount < known.pageCount) {
            settled.set(block.type, result)
        }
    }
}

/** The response with every settled block total applied. */
export function applySettled(response: SearchResponse, settled: SettledTotals): SearchResponse {
    if (!response.blocks.some((block) => settled.has(block.type))) {
        return response
    }

    return {
        ...response,
        blocks: response.blocks.map((block) => {
            const result = settled.get(block.type)

            return result && isPaged(block) ? { ...block, total: result.total, pageCount: result.pageCount } : block
        }),
    }
}

/** True when every paged block's total is exact; blocks without paging count their items. */
export function isExactTotal(response: SearchResponse, settled: SettledTotals): boolean {
    return response.blocks.every((block) => !isPaged(block) || settled.get(block.type)?.exact === true)
}
